// @ts-nocheck
/**
 * Cook mode, one implementation for the recipe page, the feed's recipe sheet and the planner (styles: assets/vhp-cook.css).
 * Ingredients card (tickable, ticks kept per recipe until reload), then one card per step with the ingredients that
 * step names and a timer when the text says "N Min" / "N Std". The timer keeps running across steps, vibrates + beeps
 * at zero. Screen stays awake (Wake Lock) while open. A modal <dialog>, so it also sits above the planner's sheets.
 * Opening pushes a history entry: the phone's back button closes it.
 *
 *   window.vhpCook.open({
 *     key, title, source,            // key: ticks are kept per key (none: not kept); source: analytics
 *     done: [index],                 // optional, ingredients already ticked (first open of a key only)
 *     ingredients: [{ name, amount, note, group }],
 *     steps: [{ title, html, tip }],  // title / tip: text, html: the step text as HTML
 *     actions: [element],            // optional, put in the top bar (feed: "+ Plan")
 *     rateId,                        // optional product id: "Fertig" asks for a star rating (assets/vhp-rating.js)
 *     plan,                          // optional element for the "Fertig" screen, under the rating (planner card)
 *     onDone(),                      // optional: "Fertig" on the last step was tapped
 *     onClose(ticked),               // ticked: ingredient indexes ticked at close
 *   })
 *   window.vhpCook.read(root, selectors)  // { ingredients, steps } from rendered recipe markup, see READERS
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpCook) return;

  const CLOSE_ICON =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M6 18L18 6"/></svg>';
  const CHECK_ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  const TIMER_ICON =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2M9.5 2.5h5"/></svg>';

  const esc = (text) => String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const clean = (text) => String(text || '').replace(/\s+/g, ' ').trim();

  /* ---------------------------------------------------------------- reading recipe markup */

  // group / ing are rows of one list; name's first text node is the name.
  const READERS = {
    // The recipe sheet and the recipe page (snippets/vhp-recipe-body.liquid), planner own recipes.
    sheet: {
      group: '.vhp-recipe-ings__group',
      ing: '.vhp-recipe-ings__item',
      amount: '.vhp-recipe-ings__amt',
      name: '.vhp-recipe-ings__name',
      note: '.vhp-recipe-ings__note',
      done: '.is-on',
      step: '.vhp-recipe-steps__item',
      stepTitle: '.vhp-recipe-steps__title',
      stepText: '.vhp-recipe-steps__text',
      tip: '.vhp-recipe-steps__tip',
    },
  };

  const read = (root, selectors) => {
    const sel = typeof selectors === 'string' ? READERS[selectors] : selectors;
    const ingredients = [];
    const done = [];
    let group = '';
    root.querySelectorAll(`${sel.group}, ${sel.ing}`).forEach((row) => {
      if (row.matches(sel.group)) {
        group = clean(row.textContent);
        return;
      }
      const nameEl = row.querySelector(sel.name);
      const noteEl = nameEl?.querySelector(sel.note) || row.querySelector(sel.note);
      // The name is the text before the note.
      let name = '';
      nameEl?.childNodes.forEach((node) => {
        if (node === noteEl || noteEl?.contains(node) || node.contains?.(noteEl)) return;
        name += node.textContent;
      });
      name = clean(name);
      if (!name) return;
      if (sel.done && row.matches(sel.done)) done.push(ingredients.length);
      // "0" / "0.0 g" (salt and pepper to taste) shows no amount.
      let amount = clean(row.querySelector(sel.amount)?.textContent);
      if (/^0+([.,]0+)?(s*D.*)?$/.test(amount) && !/^0[.,]d*[1-9]/.test(amount)) amount = '';
      ingredients.push({ name, group, amount, note: clean(noteEl?.textContent) });
    });
    const steps = [...root.querySelectorAll(sel.step)].map((step) => {
      // The tip's icon (recipe page, feed sheet) stays out: the card puts its own.
      const tipEl = step.querySelector(sel.tip);
      const tip = clean(tipEl?.textContent).replace(/^\p{Extended_Pictographic}️?\s*/u, '');
      return {
        title: clean(step.querySelector(sel.stepTitle)?.textContent),
        html: step.querySelector(sel.stepText)?.innerHTML || '',
        tip,
      };
    });
    return { ingredients, steps, done };
  };

  /* ---------------------------------------------------------------- matching steps to ingredients */

  // Two letters is enough for "Öl" / "Ei"; short words only ever match exactly.
  const cookWords = (text) =>
    text.toLocaleLowerCase('de').replace(/ß/g, 'ss').split(/[^a-zäöü]+/).filter((word) => word.length >= 2);

  // An ingredient is "in this step" when the text names it: its last word, a text word it starts with
  // ("Knoblauch" → Knoblauchpulver), or a text word starting with its stem ("Zwiebeln" → Zwiebel).
  // A named group ("Marinade zubereiten" → group Marinade) brings all of its ingredients, but only in the
  // first step that names it: a later "die Marinade dazugeben" means the finished marinade.
  const namesIngredient = (words, ing) => {
    const word = cookWords(ing.name).pop();
    if (!word) return false;
    const stem = word.slice(0, Math.max(5, word.length - 3));
    return words.some((tw) => tw === word || (tw.length >= 5 && word.startsWith(tw)) || (word.length >= 5 && tw.startsWith(stem)));
  };
  const namesGroup = (words, group) => {
    const word = cookWords(group).pop();
    return Boolean(word && word.length >= 4 && words.some((tw) => tw === word || tw.startsWith(word)));
  };

  // First "10 Min" / "8-10 Minuten" / "1 Std" in a step → minutes (lower bound).
  const stepMinutes = (text) => {
    const match = text.match(/(\d+)\s*(?:[-–]\s*\d+\s*)?(min\b|minuten|minute|std\b|stunden?|hours?\b|hrs?\b)/i);
    if (!match) return 0;
    const n = Number(match[1]) * (/^(std|stunde|hour|hr)/i.test(match[2]) ? 60 : 1);
    return n > 0 && n <= 480 ? n : 0;
  };

  const textOf = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  };

  /* ---------------------------------------------------------------- wake lock, timer */

  let dlg = null;
  let current = null; // { id, opts, index, count, done }
  let wakeLock = null;
  let timer = null;
  let audio = null;
  const ticked = new Map(); // recipe key → Set of ingredient indexes

  const acquireWakeLock = async () => {
    try {
      if (!('wakeLock' in navigator) || wakeLock || document.visibilityState !== 'visible') return;
      wakeLock = await navigator.wakeLock.request('screen');
      wakeLock.addEventListener('release', () => {
        wakeLock = null;
      });
      dlg?.querySelector('[data-cook-awake]')?.removeAttribute('hidden');
    } catch (_) {
      wakeLock = null; // battery saver / unsupported: cooking works, the screen may just dim
    }
  };

  const releaseWakeLock = () => {
    try {
      wakeLock?.release();
    } catch (_) {}
    wakeLock = null;
  };

  document.addEventListener('visibilitychange', () => {
    if (current && document.visibilityState === 'visible') acquireWakeLock();
  });

  const fmtClock = (ms) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
  };

  const fmtMinutes = (n) => (n >= 60 && n % 60 === 0 ? tr('common.hours', { n: n / 60 }) : tr('common.min', { n }));

  // The AudioContext is created on the timer tap so iOS lets it play when the timer runs out.
  const unlockAudio = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!audio && Ctx) audio = new Ctx();
      audio?.resume?.();
    } catch (_) {
      audio = null;
    }
  };

  const beep = () => {
    try {
      if (!audio) return;
      [0, 0.35, 0.7].forEach((at) => {
        const osc = audio.createOscillator();
        const gain = audio.createGain();
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.25, audio.currentTime + at);
        gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + at + 0.25);
        osc.connect(gain).connect(audio.destination);
        osc.start(audio.currentTime + at);
        osc.stop(audio.currentTime + at + 0.26);
      });
    } catch (_) {}
  };

  const paintTimer = () => {
    const pill = dlg?.querySelector('[data-cook-timer]');
    if (!pill) return;
    pill.hidden = !timer;
    if (!timer) return;
    const left = timer.end - Date.now();
    pill.classList.toggle('is-done', left <= 0);
    pill.querySelector('[data-cook-timer-time]').textContent = left <= 0 ? tr('cook.time_up') : fmtClock(left);
    pill.querySelector('[data-cook-timer-label]').textContent = timer.label;
  };

  const stopTimer = () => {
    if (timer) window.clearInterval(timer.tick);
    timer = null;
    paintTimer();
    dlg?.querySelectorAll('[data-cook-timer-start]').forEach((btn) => btn.classList.remove('is-running'));
  };

  const startTimer = (minutes, card, label) => {
    stopTimer();
    unlockAudio();
    timer = {
      end: Date.now() + minutes * 60000,
      card,
      label,
      rang: false,
      tick: window.setInterval(() => {
        if (!timer) return;
        if (!timer.rang && Date.now() >= timer.end) {
          timer.rang = true;
          navigator.vibrate?.([300, 150, 300, 150, 300]);
          beep();
        }
        paintTimer();
      }, 500),
    };
    dlg?.querySelectorAll('[data-cook-timer-start]').forEach((btn) => btn.classList.toggle('is-running', Number(btn.dataset.cookCard) === card));
    paintTimer();
  };

  /* ---------------------------------------------------------------- cards */

  const ingredientsCard = (ings, done) => {
    let lastGroup = '';
    const rows = ings
      .map((ing, i) => {
        const head = ing.group && ing.group !== lastGroup ? `<li class="cook-ing-group">${esc(ing.group)}</li>` : '';
        lastGroup = ing.group;
        const on = done.has(i);
        return `${head}<li class="cook-ing${on ? ' is-done' : ''}" role="checkbox" tabindex="0" aria-checked="${on}" data-cook-ing="${i}">
            <span class="cook-ing__box">${CHECK_ICON}</span>
            <span class="cook-ing__amt">${esc(ing.amount)}</span>
            <span class="cook-ing__name">${esc(ing.name)}${ing.note ? `<small>${esc(ing.note)}</small>` : ''}</span>
          </li>`;
      })
      .join('');
    return `<article class="cook-card" data-cook-card>
        <p class="cook-card__kicker">${tr('cook.prep')} · <span data-cook-ing-count></span></p>
        <h2 class="cook-card__title">${tr('cook.ingredients')}</h2>
        <p class="cook-card__lead">${tr('cook.lead')}</p>
        <ul class="cook-ings" role="list">${rows}</ul>
      </article>`;
  };

  const stepCards = (steps, ings, first) => {
    const total = steps.length;
    const groupsShown = new Set();
    return steps.map((step, index) => {
      const words = cookWords(`${step.title} ${textOf(step.html)}`);
      const groups = [...new Set(ings.map((ing) => ing.group))].filter((g) => g && !groupsShown.has(g) && namesGroup(words, g));
      groups.forEach((g) => groupsShown.add(g));
      // A named group wins over the same ingredient from another group ("Seitan Teig" step: its 300 ml Brühe,
      // not also the marinade's 100 ml). Same name + amount twice shows once.
      const fromGroups = ings.filter((ing) => groups.includes(ing.group));
      const groupNames = new Set(fromGroups.map((ing) => ing.name));
      const uses = [...fromGroups, ...ings.filter((ing) => !groups.includes(ing.group) && !groupNames.has(ing.name) && namesIngredient(words, ing))]
        .sort((a, b) => ings.indexOf(a) - ings.indexOf(b))
        .filter((ing, i, list) => list.findIndex((row) => row.name === ing.name && row.amount === ing.amount) === i);
      const minutes = stepMinutes(textOf(step.html));
      const usesHtml = uses.length
        ? `<div class="cook-uses">
             <p class="cook-uses__label">${tr('cook.you_need')}</p>
             <ul class="cook-uses__list" role="list">${uses
               .map((ing) => `<li class="cook-use">${ing.amount ? `<b>${esc(ing.amount)}</b> ` : ''}${esc(ing.name)}</li>`)
               .join('')}</ul>
           </div>`
        : '';
      const timerHtml = minutes
        ? `<button type="button" class="cook-timer-btn" data-cook-timer-start="${minutes}" data-cook-card="${first + index}" data-cook-step="${index + 1}">
             ${TIMER_ICON}<span>${tr('cook.timer', { time: fmtMinutes(minutes) })}</span><em>${tr('cook.timer_running')}</em>
           </button>`
        : '';
      return `<article class="cook-card" data-cook-card>
          <p class="cook-card__kicker">${tr('cook.step_of', { n: index + 1, total })}</p>
          ${step.title ? `<h2 class="cook-card__title">${esc(step.title)}</h2>` : ''}
          <div class="cook-card__text">${step.html}</div>
          ${timerHtml}
          ${usesHtml}
          ${step.tip ? `<p class="cook-tip"><span aria-hidden="true">💡</span> ${esc(step.tip)}</p>` : ''}
        </article>`;
    });
  };

  /* ---------------------------------------------------------------- open / close */

  const isOpen = () => Boolean(current);

  const paint = () => {
    if (!current) return;
    const { index, count, opts, done } = current;
    dlg.querySelectorAll('[data-cook-dots] i').forEach((dot, i) => dot.classList.toggle('is-on', i <= index));
    dlg.querySelector('[data-cook-prev]').disabled = index === 0;
    dlg.querySelector('[data-cook-next]').textContent =
      index === count - 1 ? tr('cook.finish') : opts.ingredients.length && index === 0 ? tr('cook.start') : tr('common.next');
    const ingCount = dlg.querySelector('[data-cook-ing-count]');
    if (ingCount) ingCount.textContent = tr('cook.ready_count', { done: done.size, total: opts.ingredients.length });
  };

  const go = (i) => {
    if (!current) return;
    const track = dlg.querySelector('[data-cook-track]');
    const target = Math.max(0, Math.min(current.count - 1, i));
    track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' });
  };

  // "Fertig" on the last step: the moment they actually cooked it, so the one place that asks for a rating unprompted.
  // Shown once per recipe and device, never again for 14 days after "Überspringen". The caller's opts.plan (a card,
  // assets/vhp-plan-goal.js cookCard) goes under it, and shows even when there is nothing to rate. opts.onDone hears the
  // tap. False: nothing to show, just close.
  const showDone = () => {
    try {
      current?.opts.onDone?.();
    } catch (_) {}
    const id = current?.opts.rateId;
    const ask = Boolean(id && window.vhpRating?.canAsk(id));
    const plan = current?.opts.plan || null;
    if (!ask && !plan) return false;
    const panel = document.createElement('div');
    panel.className = `cook__done${plan ? ' has-plan' : ''}`;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-labelledby', 'cook-done-title');
    panel.innerHTML = `<span class="cook__done-badge">${CHECK_ICON}</span>
      <h2 class="cook__done-title" id="cook-done-title">${tr('rating.cook_title')}</h2>
      ${ask ? `<p class="cook__done-text">${tr('rating.cook_text')}</p><div data-cook-rate></div>` : ''}
      <button type="button" class="cook__done-skip" data-cook-done-skip>${ask ? tr('rating.skip') : tr('common.close')}</button>`;
    const skipBtn = panel.querySelector('[data-cook-done-skip]');
    if (plan) panel.insertBefore(plan, skipBtn);
    // The same rating block as under every recipe (snippets/vhp-reviews.liquid): after the tap the optional written
    // review opens right here, so no auto-close; "Schließen" ends it.
    if (ask) {
      panel.querySelector('[data-cook-rate]').appendChild(
        window.vhpRating.block({
          id,
          source: `cook_${current.opts.source || ''}`,
          title: false,
          text: false,
          onDone: () => {
            panel.classList.add('is-rated');
            skipBtn.textContent = tr('common.close');
          },
        })
      );
    }
    skipBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      if (ask && !panel.classList.contains('is-rated')) window.vhpRating.skip();
      close();
    });
    dlg.appendChild(panel);
    current.doneShown = true;
    return true;
  };

  const closeUi = () => {
    if (!current) return;
    const { opts, done: ticks } = current;
    current = null;
    stopTimer();
    releaseWakeLock();
    const node = dlg;
    dlg = null;
    if (node) {
      node.classList.remove('is-in');
      window.setTimeout(() => {
        if (node.open) node.close();
        node.remove();
      }, 220);
    }
    try {
      opts.onClose?.([...ticks]);
    } catch (_) {}
    // For whoever waits on cook mode (the giveaway popup's recipe hold).
    document.dispatchEvent(new CustomEvent('vhp:cook-close'));
  };

  // Close through history when our entry is on top, so back and the ✕ end in the same place. Each opening has its
  // own id: a marker left over from a reload never counts.
  const close = () => {
    if (!current) return;
    if (history.state?.vhpCook === current.id) history.back();
    else closeUi();
  };

  window.addEventListener('popstate', () => {
    if (current && history.state?.vhpCook !== current.id) closeUi();
  });

  const toggleIng = (row) => {
    const i = Number(row.dataset.cookIng);
    const { done } = current;
    const on = !done.has(i);
    if (on) done.add(i);
    else done.delete(i);
    row.classList.toggle('is-done', on);
    row.setAttribute('aria-checked', String(on));
    paint();
  };

  const open = (opts) => {
    if (current || !opts?.steps?.length) return;
    const ingredients = opts.ingredients || [];
    const key = opts.key || '';
    if (key && !ticked.has(key)) ticked.set(key, new Set(opts.done || []));
    const done = key ? ticked.get(key) : new Set(opts.done || []);

    const cards = [];
    if (ingredients.length) cards.push(ingredientsCard(ingredients, done));
    cards.push(...stepCards(opts.steps, ingredients, cards.length));

    dlg = document.createElement('dialog');
    dlg.className = 'cook';
    dlg.setAttribute('aria-label', opts.title ? tr('cook.mode_of', { title: opts.title }) : tr('cook.mode'));
    dlg.innerHTML = `<div class="cook__bar">
        <div class="cook__head">
          <p class="cook__title">${esc(opts.title || '')}</p>
          <div class="cook__dots" data-cook-dots>${cards
            .map((_, i) => {
              // A tap on a bar jumps to that card: they looked tappable (docs/UX_REVIEW.md #7).
              const label = ingredients.length && i === 0 ? tr('cook.ingredients') : tr('cook.step', { n: ingredients.length ? i : i + 1 });
              return `<button type="button" data-cook-go="${i}" aria-label="${esc(label)}"><i></i></button>`;
            })
            .join('')}</div>
        </div>
        <span class="cook__awake" data-cook-awake hidden>${tr('cook.awake')}</span>
        <button type="button" class="cook__close" data-cook-close aria-label="${tr('cook.exit')}">${CLOSE_ICON}</button>
      </div>
      <div class="cook__track" data-cook-track>${cards.join('')}</div>
      <div class="cook__timer" data-cook-timer role="timer" hidden>
        <button type="button" class="cook__timer-main" data-cook-timer-jump aria-label="${tr('cook.timer_jump')}">
          ${TIMER_ICON}<span class="cook__timer-time" data-cook-timer-time></span><span class="cook__timer-label" data-cook-timer-label></span>
        </button>
        <button type="button" class="cook__timer-stop" data-cook-timer-stop aria-label="${tr('cook.timer_stop')}">${CLOSE_ICON}</button>
      </div>
      <div class="cook__nav">
        <button type="button" class="cook__btn" data-cook-prev>${tr('common.back')}</button>
        <button type="button" class="cook__btn cook__btn--primary" data-cook-next>${tr('common.next')}</button>
      </div>`;
    (opts.actions || []).forEach((node) => node && dlg.querySelector('.cook__bar').insertBefore(node, dlg.querySelector('[data-cook-close]')));
    if (!opts.title) dlg.querySelector('.cook__title').remove();

    current = { id: Date.now(), opts: { ...opts, ingredients }, index: 0, count: cards.length, done };
    document.body.appendChild(dlg);

    const track = dlg.querySelector('[data-cook-track]');
    track.addEventListener('scroll', () => {
      const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (current && i !== current.index) {
        current.index = i;
        paint();
      }
    });
    dlg.addEventListener('click', (event) => {
      const t = event.target;
      const row = t.closest('[data-cook-ing]');
      if (row) {
        toggleIng(row);
        return;
      }
      const start = t.closest('[data-cook-timer-start]');
      if (start) {
        const card = Number(start.dataset.cookCard);
        if (timer?.card === card) stopTimer();
        else startTimer(Number(start.dataset.cookTimerStart), card, tr('cook.step', { n: start.dataset.cookStep }));
        return;
      }
      if (t.closest('[data-cook-timer-stop]')) {
        stopTimer();
        return;
      }
      if (t.closest('[data-cook-timer-jump]') && timer) {
        if (timer.end <= Date.now()) stopTimer();
        else go(timer.card);
        return;
      }
      const dot = t.closest('[data-cook-go]');
      if (dot) {
        go(Number(dot.dataset.cookGo));
        return;
      }
      if (t.closest('[data-cook-prev]')) go(current.index - 1);
      else if (t.closest('[data-cook-next]')) {
        if (current.index === current.count - 1) {
          if (current.doneShown || !showDone()) close();
        }
        else go(current.index + 1);
      } else if (t.closest('[data-cook-close]')) close();
    });
    dlg.addEventListener('keydown', (event) => {
      const row = event.target.closest?.('[data-cook-ing]');
      if (row && (event.key === ' ' || event.key === 'Enter')) {
        event.preventDefault();
        toggleIng(row);
      } else if (event.key === 'ArrowRight') go(current.index + 1);
      else if (event.key === 'ArrowLeft') go(current.index - 1);
    });
    // Escape: through history like the ✕.
    dlg.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    dlg.showModal();
    paint();
    try {
      history.pushState({ ...(history.state || {}), vhpCook: current.id }, '', location.href);
    } catch (_) {}
    acquireWakeLock();
    if (typeof window.vhpTrack === 'function') window.vhpTrack('recipe_cook_mode', { recipe_url: key || location.pathname, source: opts.source || '' });
    window.requestAnimationFrame(() => dlg?.classList.add('is-in'));
  };

  window.vhpCook = { open, close, isOpen, read };
})();
